import React from "react";
import { ChevronDownRegular } from "@fluentui/react-icons/svg/chevron-down";
import { ChevronRightRegular } from "@fluentui/react-icons/svg/chevron-right";

export interface SidebarSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  /** The whole pane is collapsed to its rail, so there is no room for a heading. */
  isPaneCollapsed: boolean;
  children: React.ReactNode;
}

/**
 * One foldable section of the filter pane.
 *
 * Purely presentational: whether it is open lives in the owning section's
 * ViewModel, so this stays a wrapper with no state of its own.
 *
 * Collapsed to the rail there is no heading to click, so the section always
 * renders its rows — folding is a thing you do to a pane you can read. A rule
 * stands in for the heading there, or one section's icons ran straight into
 * the next.
 */
const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  isOpen,
  onToggle,
  isPaneCollapsed,
  children,
}) => {
  if (isPaneCollapsed) {
    return (
      <>
        <div className="sidebar-nav__divider sidebar-nav__divider--rail" />
        {children}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="sidebar-nav__title sidebar-nav__title--button"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <ChevronDownRegular className="sidebar-nav__title-chevron" />
        ) : (
          <ChevronRightRegular className="sidebar-nav__title-chevron" />
        )}
        <span>{title}</span>
      </button>

      {isOpen && children}
    </>
  );
};

export default SidebarSection;
